import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { apiEndpoints } from '../config/api';

const featuredListingsEndpoint = apiEndpoints.featuredListings;
const propertyListingsEndpoint = apiEndpoints.propertyListings;
const enquiryEndpoint = apiEndpoints.propertyEnquiry;

const emptySearchFilters = {
  community: '',
  property_category: '',
  unit_type: '',
  developer: '',
  city: '',
  min_price: '',
  max_price: ''
};

const emptyContactForm = {
  name: '',
  email: '',
  phone: '',
  interest: 'Off-plan launch - Investment - Advisory',
  message: '',
  website: ''
};

const slugifyPathSegment = (value, fallback) => {
  const slug = String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || fallback;
};

const propertyDetailsUrl = (property) => {
  const developer = slugifyPathSegment(property.developer, 'developer');
  const seoSlug = slugifyPathSegment(
    property.seo_slug || property.url || property.title || property.reference_number,
    String(property.reference_number || property.id)
  );
  return `/new-projects/${developer}/${seoSlug}`;
};

const formatPropertyPrice = (value) => {
  const price = Number.parseFloat(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(price) && price > 0
    ? `AED ${Math.round(price).toLocaleString('en-US')}`
    : 'Price on request';
};

const AnimatedStat = ({ target, prefix = '', suffix = '', active }) => {
  const [value, setValue] = useState(active ? target : 0);

  useEffect(() => {
    if (!active) return undefined;
    const duration = 1800;
    const startedAt = performance.now();
    let frame;
    const animate = (now) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      setValue(target * (1 - Math.pow(1 - progress, 3)));
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [active, target]);

  return <strong>{prefix}{Math.round(value).toLocaleString('en-US')}{suffix}</strong>;
};

const Home = () => {
  const [filterOpen, setFilterOpen] = useState(false);
  const [advisorVisible, setAdvisorVisible] = useState(false);
  const [statsActive, setStatsActive] = useState(false);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [featuredError, setFeaturedError] = useState('');
  const [searchFilters, setSearchFilters] = useState(emptySearchFilters);
  const [communitySuggestionsOpen, setCommunitySuggestionsOpen] = useState(false);
  const [contactForm, setContactForm] = useState(emptyContactForm);
  const [contactFormState, setContactFormState] = useState({ submitting: false, type: '', message: '' });
  const [filterOptions, setFilterOptions] = useState({
    communities: [],
    communityLocations: [],
    propertyCategories: [],
    unitTypes: { residential: [], commercial: [] },
    developers: [],
    cities: [],
  });
  const statsRef = useRef(null);

  const updateContactForm = (event) => {
    const { name, value } = event.target;
    setContactForm((current) => ({ ...current, [name]: value }));
  };

  const submitContactForm = async (event) => {
    event.preventDefault();
    setContactFormState({ submitting: true, type: '', message: '' });

    try {
      const response = await fetch(enquiryEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...contactForm,
          source: 'Home Page',
          propertyType: '',
          propertyTitle: 'Home page enquiry',
          referenceNumber: '',
          pageUrl: window.location.href
        })
      });
      const payload = await response.json();
      if (!response.ok || payload.result !== 1) {
        throw new Error(payload.message || 'We could not send your enquiry.');
      }

      setContactForm(emptyContactForm);
      setContactFormState({
        submitting: false,
        type: 'success',
        message: 'Thank you. Your enquiry has been sent to our advisory team.'
      });
    } catch (submissionError) {
      setContactFormState({
        submitting: false,
        type: 'error',
        message: submissionError.message || 'We could not send your enquiry. Please try again.'
      });
    }
  };

  useEffect(() => {
    document.body.classList.add('elevate-home');
    return () => document.body.classList.remove('elevate-home', 'modal-open');
  }, []);

  useEffect(() => {
    document.body.classList.toggle('modal-open', filterOpen);
  }, [filterOpen]);

  useEffect(() => {
    const updateScrollState = () => {
      const pageBottomDistance = document.documentElement.scrollHeight - (window.scrollY + window.innerHeight);
      setAdvisorVisible(window.scrollY > 60 && pageBottomDistance > 180);
    };
    updateScrollState();
    window.addEventListener('scroll', updateScrollState, { passive: true });
    return () => window.removeEventListener('scroll', updateScrollState);
  }, []);

  useEffect(() => {
    const closeWithEscape = (event) => {
      if (event.key === 'Escape') setFilterOpen(false);
    };
    document.addEventListener('keydown', closeWithEscape);
    return () => document.removeEventListener('keydown', closeWithEscape);
  }, []);

  useEffect(() => {
    const section = statsRef.current;
    if (!section || !('IntersectionObserver' in window)) {
      setStatsActive(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setStatsActive(true);
        observer.disconnect();
      }
    }, { threshold: 0.35 });
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    window.AOS?.init({ duration: 800, once: true, offset: 80 });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetch(featuredListingsEndpoint, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: controller.signal
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Featured listings request failed with ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        setFeaturedListings(Array.isArray(payload?.data) ? payload.data.slice(0, 3) : []);
        setFeaturedError('');
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setFeaturedListings([]);
        setFeaturedError('Featured properties are temporarily unavailable.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setFeaturedLoading(false);
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${propertyListingsEndpoint}?optionsOnly=1`, {
      headers: { Accept: 'application/json' },
      signal: controller.signal
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Property options request failed with ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        const options = payload?.data || {};
        setFilterOptions({
          communities: Array.isArray(options.communities) ? options.communities : [],
          communityLocations: Array.isArray(options.communityLocations) ? options.communityLocations : [],
          propertyCategories: Array.isArray(options.propertyCategories) ? options.propertyCategories : [],
          unitTypes: {
            residential: Array.isArray(options.unitTypes?.residential) ? options.unitTypes.residential : [],
            commercial: Array.isArray(options.unitTypes?.commercial) ? options.unitTypes.commercial : []
          },
          developers: Array.isArray(options.developers) ? options.developers : [],
          cities: Array.isArray(options.cities) ? options.cities : [],
        });
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          setFilterOptions({
            communities: [],
            communityLocations: [],
            propertyCategories: [],
            unitTypes: { residential: [], commercial: [] },
            developers: [],
            cities: [],
          });
        }
      });
    return () => controller.abort();
  }, []);

  const updateSearchFilter = (event) => {
    const { name, value } = event.target;
    setSearchFilters((current) => ({ ...current, [name]: value }));
  };

  const submitPropertySearch = (event) => {
    event.preventDefault();
    const params = new URLSearchParams();
    Object.entries(searchFilters).forEach(([key, value]) => {
      if (String(value).trim()) params.set(key, String(value).trim());
    });
    window.location.href = `/new-projects${params.toString() ? `?${params}` : ''}`;
  };

  const resetPropertySearch = () => setSearchFilters(emptySearchFilters);
  const communityQuery = searchFilters.community.trim().toLowerCase();
  const matchedCommunities = communityQuery
    ? filterOptions.communities
        .filter((community) => community.toLowerCase().includes(communityQuery))
        .slice(0, 7)
    : [];

  const selectCommunity = (community) => {
    const location = filterOptions.communityLocations.find(
      (item) => item.community.toLowerCase() === community.toLowerCase()
    );
    setSearchFilters((current) => ({
      ...current,
      community,
      city: location?.city || ''
    }));
    setCommunitySuggestionsOpen(false);
  };

  const updateCityFilter = (event) => {
    const city = event.target.value;
    setSearchFilters((current) => {
      const selectedCommunity = filterOptions.communityLocations.find(
        (item) => item.community.toLowerCase() === current.community.trim().toLowerCase()
      );
      const communityIsCompatible = !current.community || !city || !selectedCommunity?.city
        || selectedCommunity.city.toLowerCase() === city.toLowerCase();
      return {
        ...current,
        city,
        community: communityIsCompatible ? current.community : ''
      };
    });
  };

  const updatePropertyCategory = (event) => {
    const propertyCategory = event.target.value;
    setSearchFilters((current) => {
      const allowedTypes = propertyCategory === 'Commercial'
        ? filterOptions.unitTypes.commercial
        : propertyCategory === 'Residential'
          ? filterOptions.unitTypes.residential
          : [...filterOptions.unitTypes.residential, ...filterOptions.unitTypes.commercial];
      return {
        ...current,
        property_category: propertyCategory,
        unit_type: allowedTypes.includes(current.unit_type) ? current.unit_type : ''
      };
    });
  };

  const updateUnitType = (event) => {
    const unitType = event.target.value;
    const propertyCategory = filterOptions.unitTypes.commercial.includes(unitType)
      ? 'Commercial'
      : filterOptions.unitTypes.residential.includes(unitType)
        ? 'Residential'
        : searchFilters.property_category;
    setSearchFilters((current) => ({
      ...current,
      unit_type: unitType,
      property_category: unitType ? propertyCategory : current.property_category
    }));
  };

  const availableUnitTypes = searchFilters.property_category === 'Commercial'
    ? filterOptions.unitTypes.commercial
    : searchFilters.property_category === 'Residential'
      ? filterOptions.unitTypes.residential
      : [...filterOptions.unitTypes.residential, ...filterOptions.unitTypes.commercial];
  useEffect(() => {
    const scriptId = 'elfsight-platform-script';

    let script = document.getElementById(scriptId);

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://elfsightcdn.com/platform.js';
      script.async = true;
      document.body.appendChild(script);
    }

    return () => {
      // Keep the script loaded for React route navigation.
      // Do not remove Elfsight's global script here.
    };
  }, []);
  return (
  <>
        <Header />

        <main id="top">
            <section className="hero" aria-label="Elevate Realty introduction">
                <video className="hero-media" autoPlay muted loop playsInline poster="/assets/img/hero-bg-7.jpg">
                    <source src="/assets/video/hero-loop.mp4" type="video/mp4" />
        </video>
                <div className="hero-overlay"></div>

                <div className="hero-inner">
         
                    <h1>
                        <span>We don't               <span className="hero-word-accent">just</span> sell.</span>
                        <span>We               <em>advise.</em></span>
          </h1>
                    <p className="hero-copy">
            A boutique advisory representing landmark residences, private villas and off-plan investments
            across Dubai's most sought-after addresses.
          </p>

                    <div className="legacy-search">
                        <div className="legacy-tabs" aria-label="Property search type">
                            <span>Off-plan</span>
            </div>
                        <form className="legacy-search-form" onSubmit={submitPropertySearch}>
                            <div className="legacy-field legacy-field--autocomplete">
                                <span>Location</span>
                                <input
                                  className="legacy-location-input"
                                  type="text"
                                  name="community"
                                  value={searchFilters.community}
                                  onChange={(event) => {
                                    setSearchFilters((current) => ({
                                      ...current,
                                      community: event.target.value,
                                      city: ''
                                    }));
                                    setCommunitySuggestionsOpen(true);
                                  }}
                                  onFocus={() => setCommunitySuggestionsOpen(true)}
                                  onBlur={() => setCommunitySuggestionsOpen(false)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Escape') setCommunitySuggestionsOpen(false);
                                  }}
                                  placeholder="Area, development..."
                                  autoComplete="off"
                                  aria-label="Search location"
                                  role="combobox"
                                  aria-autocomplete="list"
                                  aria-controls="home-community-options"
                                  aria-expanded={communitySuggestionsOpen && matchedCommunities.length > 0}
                                />
                                {communitySuggestionsOpen && matchedCommunities.length > 0 && (
                                  <div className="legacy-autocomplete-menu" id="home-community-options" role="listbox">
                                    {matchedCommunities.map((community) => (
                                      <button
                                        key={community}
                                        type="button"
                                        role="option"
                                        aria-selected={searchFilters.community === community}
                                        onMouseDown={(event) => {
                                          event.preventDefault();
                                          selectCommunity(community);
                                        }}
                                      >
                                        {community}
                                      </button>
                                    ))}
                                  </div>
                                )}
              </div>
                            <button type="button" className="legacy-advanced" aria-haspopup="dialog" onClick={() => setFilterOpen(true)}>
                                <span>Filters</span>
                                <i className="fa-solid fa-sliders"></i>
              </button>
                            <button type="submit" className="legacy-submit">
                                <i className="fa-solid fa-magnifying-glass"></i>
                                <span>Search</span>
              </button>
            </form>
          </div>

                    <a className="scroll-link" href="#properties">Scroll to explore</a>
        </div>
      </section>

            <section className="ticker" id="trusted-developers" aria-label="Trusted developers">
                <p>Trusted by leading developers</p>
                <div className="ticker-marquee">
                    <div className="ticker-track">
                        <span>Select Group</span>
                        <span>Ellington</span>
                        <span>Azizi</span>
                        <span>Binghatti</span>
                        <span>Arada</span>
                        <span>Deyaar</span>
                        <span>Emaar</span>
                        <span>Damac</span>
                        <span>Sobha</span>
                        <span>Meraas</span>
                        <span>Nakheel</span>
                        <span>Omniyat</span>
          </div>
                    <div className="ticker-track" aria-hidden="true">
                        <span>Select Group</span>
                        <span>Ellington</span>
                        <span>Azizi</span>
                        <span>Binghatti</span>
                        <span>Arada</span>
                        <span>Deyaar</span>
                        <span>Emaar</span>
                        <span>Damac</span>
                        <span>Sobha</span>
                        <span>Meraas</span>
                        <span>Nakheel</span>
                        <span>Omniyat</span>
          </div>
        </div>
      </section>

            <section className="section signature" id="properties">
                <div className="section-heading">
                    <div>
                        <p className="eyebrow">Signature collection</p>
                        <h2>Residences               <em>curated</em> for a              <br />considered life.</h2>
          </div>
                    <Link to="/new-projects">View all listings</Link>
        </div>

                <div className="property-grid">
                    {featuredLoading && [1, 2, 3].map((item) => (
                      <article key={item} className="property-card" aria-label="Loading featured property">
                        <div className="property-image">
                          <span>Loading</span>
                        </div>
                        <div className="property-content">
                          <div className="property-meta"><span>Featured property</span></div>
                          <h3>Loading property...</h3>
                          <p>Please wait while the latest featured listing is loaded.</p>
                        </div>
                      </article>
                    ))}

                    {!featuredLoading && featuredListings.map((property) => {
                      const detailsUrl = propertyDetailsUrl(property);
                      const title = property.title || 'Featured property';
                      return (
                        <article className="property-card" key={property.reference_number || property.id}>
                          <a
                            className="property-image"
                            href={detailsUrl}
                            aria-label={`View details for ${title}`}
                            data-hover-label="View Details"
                          >
                            <img src={property.ImagePath || '/assets/img/property-img-1.jpg'} alt={title} loading="lazy" />
                            <span>{property.property_type || property.property_type || 'Featured'}</span>
                          </a>
                          <div className="property-content">
                            <div className="property-meta">
                              <span>{String(property.developer || 'Featured developer').trim()}</span>
                              <span>{property.community || property.city || 'Dubai'}</span>
                            </div>
                            <h3>{title}</h3>
                            <p>{property.short_description || `Discover ${title}, a featured real estate opportunity in Dubai.`}</p>
                            <div className="property-footer">
                              <strong>From {formatPropertyPrice(property.price)}</strong>
                              <Link to={detailsUrl}>View Details <i className="fa-solid fa-arrow-right" aria-hidden="true" /></Link>
                            </div>
                            <Link className="property-viewing-link" to={detailsUrl}>Request a Viewing</Link>
                          </div>
                        </article>
                      );
                    })}

                    {!featuredLoading && featuredListings.length === 0 && (
                      <div className="property-card property-content">
                        <h3>No featured properties available</h3>
                        <p>{featuredError || 'Featured properties will appear here once selected.'}</p>
                      </div>
                    )}
        </div>
      </section>

            <section className="section services" id="services">
                <div className="services-intro">
                    <div>
                        <p className="eyebrow">What we do</p>
                        <h2>An advisory built around              <br />              <em>you.</em></h2>
          </div>
                    <p>
            Elevate Realty represents principals, entrepreneurs and family offices with strategy-led
            guidance across acquisitions, off-plan allocations and private mandates.
          </p>
        </div>

                <div className="service-grid">
                    <article>
                        <span>01</span>
                        <h3>Off-Plan Advisory &amp; Acquisition</h3>
                        <p>Strategic guidance on launches, payment plans and residences aligned with long-term objectives.</p>
          </article>
                    <article>
                        <span>02</span>
                        <h3>Portfolio Management</h3>
                        <p>Curation and stewardship across developers, launches, asset classes and exit strategies.</p>
          </article>
                    <article>
                        <span>03</span>
                        <h3>Investment Structuring</h3>
                        <p>Yield-led thinking, allocation planning and local market intelligence for international clients.</p>
          </article>
                    <article>
                        <span>04</span>
                        <h3>Private Client Services</h3>
                        <p>Discreet representation for family offices, institutions and clients who value privacy.</p>
          </article>
                    <article>
                        <span>05</span>
                        <h3>Golden Visa Advisory</h3>
                        <p>Guidance on qualifying property investment and long-term UAE residency structuring.</p>
          </article>
                    <article>
                        <span>06</span>
                        <h3>Mortgage &amp; Financing</h3>
                        <p>Preferred access to local and international lenders for pre-approvals and refinancing.</p>
          </article>
        </div>
      </section>

            <section className="section about" id="about">
                <div className="about-image">
                    <img src="/assets/img/headquarters.jpg" alt="Elevate Realty headquarters" />
                    <div>
                        <p>Our headquarters</p>
                        <span>Dubai H.Q. - Representative presence in Canada &amp; India</span>
          </div>
        </div>

                <div className="about-copy">
                    <p className="eyebrow">About Elevate</p>
                    <h2>Trusted counsel for the world's most private transactions.</h2>
                    <p>
            For nearly a decade, Elevate Realty has represented residences that rarely reach the open
            market. Our approach is quiet, considered and deeply personal.
          </p>
                    <p>
            We work with a select circle of clients, placing them in homes and investments that stand the
            test of time. No inventory to push, no pressure to close. Only alignment.
          </p>
                    <div className="about-actions">
                        <Link className="primary-link" to="contact-us">Begin a conversation</Link>
                        <Link className="outline-link" to="services">Our services</Link>
          </div>
        </div>
      </section>

            <section ref={statsRef} className="stats" aria-label="Elevate Realty statistics">
                <div>
                    <AnimatedStat target={600} prefix="AED " suffix="M" active={statsActive} />
                    <span>Transacted value</span>
        </div>
                <div>
                    <AnimatedStat target={120} suffix="+" active={statsActive} />
                    <span>Residences placed</span>
        </div>
                <div>
                    <AnimatedStat target={10} active={statsActive} />
                    <span>Countries represented</span>
        </div>
                <div>
                    <AnimatedStat target={97} suffix="%" active={statsActive} />
                    <span>Repeat &amp; referred</span>
        </div>
      </section>

            <section className="section social" id="social">
                <div className="section-heading">
                    <div>
                        <p className="eyebrow">Live social feed</p>
                        <h2>Follow the               <em>conversation.</em></h2>
                        <p style={{ color: '#747474', lineHeight: '24px', paddingTop: '15px' }}>New listings, market intelligence and behind-the-scenes from our              <br />advisors — streamed live from our channels.</p>
          </div>
                    <div className="social-links">
                        <a href="https://www.instagram.com/elevaterealtyuae" target="_blank">Instagram</a>
                        <a href="https://www.linkedin.com/company/elevate-realty-uae/" target="_blank">LinkedIn</a>
                        <a href="https://www.tiktok.com/@elevaterealtyuae" target="_blank">TikTok</a>
                        <a href="https://www.youtube.com/@elevaterealty-uae" target="_blank">YouTube</a> 
          </div>
        </div>

                <div className="row">
            <div
            className="elfsight-app-cce2b04f-975b-438a-bf97-2da275f6b703"
           data-elfsight-app-lazy
          />
        </div>
                <p className="feed-note">Feed preview           <span>Connect your Instagram, LinkedIn or TikTok account to stream live posts</span></p>
      </section>

            <section className="section contact" id="contact">
                <div className="contact-heading">
                    <p className="eyebrow">Contact</p>
                    <h2>Every home begins with a conversation.</h2>
                    <p>Share a few details and one of our senior advisors will be in touch within 24 hours.</p>
        </div>

                <div className="contact-grid">
                    <a href="tel:+97145586224">
                        <i className="fa-solid fa-phone"></i>
                        <span>Office</span>
                        <strong>+971 4 558 6224</strong>
          </a>
                    <a href="tel:+971507135432">
                        <i className="fa-solid fa-mobile-alt"></i>
                        <span>Mobile</span>
                        <strong>+971 50 713 5432</strong>
          </a>
                    <a href="mailto:connect@elevaterealtyuae.com">
                        <i className="fa-regular fa-envelope"></i>
                        <span>Email</span>
                        <strong>connect@elevaterealtyuae.com</strong>
          </a>
                    <a href="https://wa.me/971507135432">
                        <i className="fa-brands fa-whatsapp"></i>
                        <span>WhatsApp</span>
                        <strong>Chat with an advisor</strong>
          </a>
        </div>

                <form className="contact-form" onSubmit={submitContactForm}>
                    <label className="contact-form-honeypot" aria-hidden="true">
                        <span>Website</span>
                        <input type="text" name="website" value={contactForm.website} tabIndex={-1} autoComplete="off" onChange={updateContactForm} />
          </label>
                    <label>
                        <span>Full name</span>
                        <input required type="text" name="name" value={contactForm.name} placeholder="Your name" autoComplete="name" onChange={updateContactForm} />
          </label>
                    <label>
                        <span>Email</span>
                        <input required type="email" name="email" value={contactForm.email} placeholder="you@example.com" autoComplete="email" onChange={updateContactForm} />
          </label>
                    <label>
                        <span>Phone</span>
                        <input required type="tel" name="phone" value={contactForm.phone} placeholder="+971 ..." autoComplete="tel" onChange={updateContactForm} />
          </label>
                    <label>
                        <span>Interest</span>
                        <select name="interest" value={contactForm.interest} onChange={updateContactForm}>
                            <option>Off-plan launch - Investment - Advisory</option>
                            <option>Private villa</option>
                            <option>Penthouse</option>
                            <option>Investment portfolio</option>
            </select>
          </label>
                    <label className="message-field">
                        <span>Message</span>
                        <textarea required name="message" value={contactForm.message} rows="5" placeholder="Tell us what you're looking for..." onChange={updateContactForm}></textarea>
          </label>
                    <button type="submit" disabled={contactFormState.submitting}>
                        {contactFormState.submitting ? 'Sending…' : 'Send enquiry'}
          </button>
                    {contactFormState.message && (
                        <p className={`contact-form-status is-${contactFormState.type}`} role="status">
                            {contactFormState.message}
            </p>
          )}
        </form>
      </section>
    </main>

        <div className={`filter-modal ${filterOpen ? 'is-open' : ''}`} aria-hidden={!filterOpen}>
            <button className="filter-modal__overlay" type="button" aria-label="Close filters" onClick={() => setFilterOpen(false)}></button>
            <section className="filter-modal__panel" role="dialog" aria-modal="true" aria-labelledby="filter-modal-title">
                <div className="filter-modal__header">
                    <div>
                        <p className="eyebrow">Refine search</p>
                        <h2 id="filter-modal-title">Filters</h2>
          </div>
                    <button className="filter-modal__close" type="button" aria-label="Close filters" onClick={() => setFilterOpen(false)}>
                        <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

                <form className="filter-form" onSubmit={(event) => {
                  setFilterOpen(false);
                  submitPropertySearch(event);
                }}> 
                    <label>
                        <span>Property Type</span>
                        <select name="property_category" value={searchFilters.property_category} onChange={updatePropertyCategory}>
                            <option value="">Residential or commercial</option>
                            {filterOptions.propertyCategories.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
          </label>
            <label>
                        <span>Unit Type</span>
                        <select name="unit_type" value={searchFilters.unit_type} onChange={updateUnitType}>
                            <option value="">Any unit type</option>
                            {availableUnitTypes.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
          </label>
                    <label>
                        <span>Min Price</span>
                        <input type="number" name="min_price" min="0" value={searchFilters.min_price} onChange={updateSearchFilter} placeholder="AED 1,000,000" />
          </label>
                    <label>
                        <span>Max Price</span>
                        <input type="number" name="max_price" min="0" value={searchFilters.max_price} onChange={updateSearchFilter} placeholder="AED 25,000,000" />
          </label>
                    <label>
                        <span>Developer</span>
                        <select name="developer" value={searchFilters.developer} onChange={updateSearchFilter}>
                            <option value="">Any developer</option>
                            {filterOptions.developers.map((developer) => <option key={developer} value={developer}>{developer}</option>)}
            </select>
          </label>
                    <label>
                        <span>City</span>
                        <select name="city" value={searchFilters.city} onChange={updateCityFilter}>
                            <option value="">Any city</option>
                            {filterOptions.cities.map((city) => <option key={city} value={city}>{city}</option>)}
            </select>
          </label>

                    <div className="filter-modal__actions">
                        <button type="button" className="filter-reset" onClick={resetPropertySearch}>Reset</button>
                        <button type="submit" className="filter-apply">Apply Filters</button>
          </div>
        </form>
      </section>
    </div>

        <Footer />

        <a className={`advisor-button ${advisorVisible ? 'is-visible' : ''}`} id="advisorButton" href="https://api.whatsapp.com/send?phone=+971507135432&amp;text=Hi" target="_blank" rel="noopener noreferrer" aria-label="Speak with an advisor on WhatsApp" aria-hidden={!advisorVisible}>
            <svg className="advisor-button__icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M21 11.5a8.5 8.5 0 0 1-12.7 7.4L3 20l1.2-5A8.5 8.5 0 1 1 21 11.5Z"></path>
      </svg>
            <span>Speak with an advisor</span>
    </a>

    
    
    
  


  </>
  );
};

export default Home;
